import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const BoardsView = ({ goals, onStatusChange, onView, onEdit, onDelete, language = 'pt' }) => {
  const [draggedCard, setDraggedCard] = useState(null);

  const getLabels = () => {
    const labels = {
      pt: {
        planned: 'Planejado',
        inProgress: 'Em Progresso',
        withMentor: 'Com Mentoria',
        followUp: 'Acompanhamento',
        statusCompleted: 'Concluído',
        archived: 'Arquivado',
        dragHere: 'Arraste metas aqui',
        dropHere: 'Solte aqui para mover',
        view: 'Visualizar',
        edit: 'Editar',
        delete: 'Excluir'
      },
      en: {
        planned: 'Planned',
        inProgress: 'In Progress',
        withMentor: 'With Mentor',
        followUp: 'Follow-up',
        statusCompleted: 'Completed',
        archived: 'Archived',
        dragHere: 'Drag goals here',
        dropHere: 'Drop here to move',
        view: 'View',
        edit: 'Edit',
        delete: 'Delete'
      }
    };
    return labels?.[language] || labels?.pt;
  };

  const labels = getLabels();

  const columns = [
    { 
      id: 'planejado', 
      title: labels?.planned,
      headerColor: 'bg-blue-50 border-blue-200',
      cardColor: 'bg-blue-100 border-blue-200',
      textColor: 'text-blue-900'
    },
    { 
      id: 'em_progresso', 
      title: labels?.inProgress,
      headerColor: 'bg-yellow-50 border-yellow-200',
      cardColor: 'bg-yellow-100 border-yellow-200',
      textColor: 'text-yellow-900'
    },
    { 
      id: 'com_mentoria', 
      title: labels?.withMentor,
      headerColor: 'bg-purple-50 border-purple-200',
      cardColor: 'bg-purple-100 border-purple-200',
      textColor: 'text-purple-900'
    },
    { 
      id: 'acompanhamento', 
      title: labels?.followUp,
      headerColor: 'bg-orange-50 border-orange-200',
      cardColor: 'bg-orange-100 border-orange-200',
      textColor: 'text-orange-900'
    },
    { 
      id: 'concluido', 
      title: labels?.statusCompleted,
      headerColor: 'bg-green-50 border-green-200',
      cardColor: 'bg-green-100 border-green-200',
      textColor: 'text-green-900'
    },
    { 
      id: 'arquivado', 
      title: labels?.archived,
      headerColor: 'bg-gray-50 border-gray-200',
      cardColor: 'bg-gray-100 border-gray-200',
      textColor: 'text-gray-900'
    }
  ];

  const areaOptions = {
    saude: language === 'en' ? 'Health' : 'Saúde',
    financas: language === 'en' ? 'Finance' : 'Finanças',
    carreira: language === 'en' ? 'Career' : 'Carreira',
    relacionamentos: language === 'en' ? 'Relationships' : 'Relacionamentos',
    pessoal: language === 'en' ? 'Personal Development' : 'Desenvolvimento Pessoal',
    familia: language === 'en' ? 'Family' : 'Família',
    lazer: language === 'en' ? 'Leisure' : 'Lazer',
    espiritualidade: language === 'en' ? 'Spirituality' : 'Espiritualidade'
  };

  const getGoalsByStatus = (status) => {
    return goals?.filter(goal => goal?.status === status) || [];
  };

  const onDragStart = (start) => {
    const goal = goals?.find(g => g?.id === start?.draggableId);
    setDraggedCard(goal);
  };

  const onDragEnd = (result) => {
    setDraggedCard(null);
    
    if (!result?.destination) {
      return;
    }

    const goalId = result?.draggableId;
    const newStatus = result?.destination?.droppableId;
    
    if (result?.source?.droppableId !== newStatus) {
      onStatusChange(goalId, newStatus);
    }
  };

  const GoalCard = ({ goal, index, column }) => (
    <Draggable draggableId={goal?.id} index={index}>
      {(provided, snapshot) => {
        // Find the column configuration for this goal's current status
        const currentColumn = columns?.find(col => col?.id === goal?.status) || column;
        
        return (
          <div
            ref={provided?.innerRef}
            {...provided?.draggableProps}
            {...provided?.dragHandleProps}
            className={`${currentColumn?.cardColor} border-2 ${currentColumn?.cardColor?.replace('bg-', 'border-')?.replace('-100', '-300')} rounded-lg p-4 mb-3 shadow-sm hover:shadow-md transition-all duration-200 cursor-move ${
              snapshot?.isDragging ? 'rotate-2 shadow-lg scale-105' : ''
            }`}
          >
            <div className="space-y-3">
              {/* Goal Title and Menu */}
              <div className="flex items-start justify-between">
                <h4 className={`font-semibold text-sm leading-tight flex-1 ${currentColumn?.textColor}`}>
                  {goal?.titulo}
                </h4>
                <div className="relative ml-2 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="MoreVertical"
                    className="h-6 w-6 p-0 hover:bg-white/50"
                    onClick={(e) => {
                      e?.stopPropagation();
                      const dropdown = e?.currentTarget?.nextElementSibling;
                      dropdown?.classList?.toggle('hidden');
                      // Close other dropdowns
                      document.querySelectorAll('[id^="dropdown-"]')?.forEach(el => {
                        if (el !== dropdown) el?.classList?.add('hidden');
                      });
                    }}
                  />
                  <div 
                    id={`dropdown-${goal?.id}`}
                    className="hidden absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[140px]"
                  >
                    <button
                      onClick={(e) => {
                        e?.stopPropagation();
                        onView(goal);
                        document?.getElementById(`dropdown-${goal?.id}`)?.classList?.add('hidden');
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2 text-gray-700"
                    >
                      <Icon name="Eye" size={14} />
                      <span>{labels?.view}</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e?.stopPropagation();
                        onEdit(goal);
                        document?.getElementById(`dropdown-${goal?.id}`)?.classList?.add('hidden');
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2 text-gray-700"
                    >
                      <Icon name="Edit" size={14} />
                      <span>{labels?.edit}</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e?.stopPropagation();
                        onDelete(goal?.id);
                        document?.getElementById(`dropdown-${goal?.id}`)?.classList?.add('hidden');
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 hover:text-red-600 flex items-center space-x-2 border-t border-gray-100"
                    >
                      <Icon name="Trash2" size={14} />
                      <span>{labels?.delete}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Area da Vida */}
              {goal?.areaVida && (
                <div className="flex items-center space-x-2">
                  <Icon name="Tag" size={12} className="text-gray-600" />
                  <span className="text-xs text-gray-700 font-medium">
                    {areaOptions?.[goal?.areaVida] || goal?.areaVida}
                  </span>
                </div>
              )}

              {/* Responsavel */}
              {goal?.responsavel && (
                <div className="flex items-center space-x-2">
                  <Icon name="User" size={12} className="text-gray-600" />
                  <span className="text-xs text-gray-700">
                    {goal?.responsavel}
                  </span>
                </div>
              )}

              {/* Data Alvo */}
              {goal?.dataAlvo && (
                <div className="flex items-center space-x-2">
                  <Icon name="Calendar" size={12} className="text-gray-600" />
                  <span className="text-xs text-gray-700">
                    {new Date(goal?.dataAlvo + 'T00:00:00')?.toLocaleDateString(language === 'pt' ? 'pt-BR' : 'en-US')}
                  </span>
                </div>
              )}

              {/* Orçamento */}
              {goal?.orcamento && (
                <div className="flex items-center space-x-2">
                  <Icon name="DollarSign" size={12} className="text-gray-600" />
                  <span className="text-xs text-gray-700">
                    R$ {goal?.orcamento}
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      }}
    </Draggable>
  );

  return (
    <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="flex gap-6 overflow-x-auto pb-6" style={{ minHeight: '600px' }}>
        {columns?.map(column => {
          const columnGoals = getGoalsByStatus(column?.id);
          
          return (
            <div key={column?.id} className="flex-shrink-0 w-80">
              {/* Column Header */}
              <div className={`${column?.headerColor} border-2 rounded-t-lg p-4`}>
                <div className="flex items-center justify-between">
                  <h3 className={`font-semibold ${column?.textColor}`}>
                    {column?.title}
                  </h3>
                  <span className={`bg-white px-2 py-1 rounded-full text-xs font-medium ${column?.textColor}`}>
                    {columnGoals?.length}
                  </span>
                </div>
              </div>
              
              {/* Column Content */}
              <Droppable droppableId={column?.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided?.innerRef}
                    {...provided?.droppableProps}
                    className={`bg-muted/20 border-l-2 border-r-2 border-b-2 ${
                      column?.headerColor?.split(' ')?.[1] || 'border-gray-200'
                    } rounded-b-lg p-4 min-h-[500px] transition-colors ${
                      snapshot?.isDraggingOver ? 'bg-muted/40' : ''
                    }`}
                  >
                    <div className="space-y-3">
                      {columnGoals?.map((goal, index) => (
                        <GoalCard key={goal?.id} goal={goal} index={index} column={column} />
                      ))}
                    </div>
                    {provided?.placeholder}
                    
                    {/* Empty State */}
                    {columnGoals?.length === 0 && !snapshot?.isDraggingOver && (
                      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                        <Icon name="Plus" size={32} className="mb-2 opacity-50" />
                        <p className="text-sm">{labels?.dragHere}</p>
                      </div>
                    )}
                    
                    {/* Drag Over Indicator */}
                    {snapshot?.isDraggingOver && (
                      <div className="flex items-center justify-center py-8 text-primary">
                        <div className="flex items-center space-x-2 p-3 border-2 border-dashed border-primary rounded-lg">
                          <Icon name="Move" size={20} />
                          <p className="font-medium">{labels?.dropHere}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
      
      {/* Drag Preview */}
      {draggedCard && (
        <div className="fixed pointer-events-none z-50 opacity-90">
          <div className="bg-card border-2 border-primary rounded-lg p-4 shadow-lg transform rotate-2">
            <h4 className="font-semibold text-foreground text-sm">
              {draggedCard?.titulo}
            </h4>
          </div>
        </div>
      )}
      
      {/* Click outside handler to close dropdowns */}
      <div 
        className="fixed inset-0 z-10 pointer-events-none"
        onClick={() => {
          document.querySelectorAll('[id^="dropdown-"]')?.forEach(el => {
            el?.classList?.add('hidden');
          });
        }}
      />
    </DragDropContext>
  );
};

export default BoardsView;